import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    CircularProgress,
    LinearProgress,
    Alert,
    Snackbar
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Sync as SyncIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { syncStocks, getStocks, getSyncStatus, type StockItem as StockType } from '../../../shared/services/stockService';
import { Loader } from '../../../shared/components/Loader';
import { SyncProgressBanner, type SyncProgressDetails } from '../../../shared/components/SyncProgressBanner';

export const Stock: React.FC = () => {
    const navigate = useNavigate();
    const [stocks, setStocks] = useState<StockType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [pagination, setPagination] = useState<{
        currentPage: number;
        totalPages: number;
        itemsPerPage: number;
    } | null>(null);
    const [syncDetails, setSyncDetails] = useState<SyncProgressDetails | null>(null);

    const loadStocks = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getStocks({
                page: 1,
                limit: 1000 // Fetch a large number for client side search/pagination as per product pattern
            });
            const responseData = response?.data;
            const data = responseData && !Array.isArray(responseData) && 'items' in responseData
                ? responseData.items
                : (Array.isArray(responseData) ? responseData : []);

            setStocks(data);
            if (responseData && !Array.isArray(responseData) && 'pagination' in responseData) {
                setPagination(responseData.pagination || null);
            }
        } catch (err: any) {
            console.error('Error fetching stocks:', err);
            setError('Failed to load stocks');
            setStocks([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStocks();
    }, [loadStocks]);

    const filteredStocks = React.useMemo(() => {
        if (!searchInput.trim()) return stocks;
        const q = searchInput.trim().toLowerCase();
        return stocks.filter(stock =>
            (stock.itemName?.toLowerCase() || '').includes(q) ||
            (stock.itemReferenceCode?.toLowerCase() || '').includes(q)
        );
    }, [stocks, searchInput]);

    const paginatedStocks = React.useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredStocks.slice(start, start + pageSize);
    }, [filteredStocks, currentPage, pageSize]);

    const handleSync = async () => {
        let pollInterval: any;
        try {
            setSyncing(true);
            setError('');
            setSyncDetails(null);

            // Start the sync process
            await syncStocks();

            // Function to check status and update UI
            const checkStatus = async () => {
                try {
                    const statusRes = await getSyncStatus();
                    if (statusRes.success) {
                        setSyncDetails(statusRes.data as any);

                        if (statusRes.data.isRunning) {
                            await loadStocks();
                        } else {
                            if (pollInterval) clearInterval(pollInterval);
                            setSyncing(false);
                            if ((statusRes.data as any).lastError) {
                                setError((statusRes.data as any).lastError);
                            } else {
                                setSuccess('Stocks synced successfully');
                            }
                            await loadStocks();
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    if (pollInterval) clearInterval(pollInterval);
                    setSyncing(false);
                }
            };

            // Immediate check to update UI right away
            await checkStatus();

            // Start polling for updates every 3 seconds
            pollInterval = setInterval(checkStatus, 3000);

        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to start sync');
            setSyncing(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(1);
    };

    const handleViewStock = (stock: StockType) => {
        navigate(`/stock/view/${stock.id}`);
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Stocks
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search stocks"
                        className="w-full sm:w-64"
                    />
                    <AppButton
                        variant="primary"
                        startIcon={<SyncIcon />}
                        onClick={handleSync}
                        disabled={syncing || loading}
                        loading={syncing}
                    >
                        {syncing ? 'Syncing...' : 'Sync Stocks'}
                    </AppButton>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {/* Sync Progress Banner */}
                    <SyncProgressBanner syncing={syncing} details={syncDetails} />

                    {loading && stocks.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && stocks.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Item Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Reference Code</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Stock</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Buffer Stock</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Outlet ID</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Updated At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" width={100}>Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredStocks.length === 0}
                                emptyColSpan={7}
                                emptyMessage="No stocks match your search."
                            >
                                {paginatedStocks.map((stock) => (
                                    <TableRow key={stock.id} hover>
                                        <TableCell>{stock.itemName}</TableCell>
                                        <TableCell>{stock.itemReferenceCode}</TableCell>
                                        <TableCell align="right">
                                            <span className={stock.stock <= stock.bufferStock ? 'text-red-600 font-bold' : ''}>
                                                {stock.stock}
                                            </span>
                                        </TableCell>
                                        <TableCell align="right">{stock.bufferStock}</TableCell>
                                        <TableCell>{stock.outletId}</TableCell>
                                        <TableCell>
                                            {stock.updatedAt
                                                ? new Date(stock.updatedAt).toLocaleDateString()
                                                : '-'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <AppButton
                                                variant="secondary"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleViewStock(stock)}
                                            >
                                                View
                                            </AppButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={filteredStocks.length}
                                page={currentPage - 1}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(1);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
